<template>
  <div class="mx-10 ma-5">
    <v-card class="pa-5">
      <v-row>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.branch_id"
            :items="branches"
            item-text="name"
            item-value="id"
            label="Branch"
            clearable
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="filters.customer_id"
            :items="customers"
            item-text="company_name"
            item-value="id"
            label="Customer"
            clearable
          />
        </v-col>
        <v-col cols="12" md="2">
          <v-btn color="primary" @click="fetchReport" :loading="loading"
            >Generate</v-btn
          >
        </v-col>
        <v-col cols="12" md="2">
          <v-btn
            color="success"
            @click="downloadPDF"
            :disabled="!customerSummary.length"
          >
            Download PDF
          </v-btn>
        </v-col>
      </v-row>
      <v-data-table
        :headers="summaryHeaders"
        :items="customerSummary"
        class="mt-5"
        hide-default-footer
      >
        <template v-slot:body.append>
          <tr>
            <td><strong>Grand Total</strong></td>
            <td>
              <strong>{{ grandTotals.total_invoice.toFixed(2) }}</strong>
            </td>
            <td>
              <strong>{{ grandTotals.paid_amount.toFixed(2) }}</strong>
            </td>
            <td>
              <strong>{{ grandTotals.due_amount.toFixed(2) }}</strong>
            </td>
          </tr>
        </template>
      </v-data-table>
      <v-alert
        v-if="!loading && customerSummary.length === 0"
        type="info"
        class="mt-5"
        outlined
      >
        No due customers found.
      </v-alert>
    </v-card>
  </div>
</template>

<script>
import { ReportService } from "@/services/report-service.js";
import { CustomerService } from "@/services/customer-service.js";
import { BranchService } from "@/services/branch-service.js";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
  name: "CustomerSummaryReport",
  data() {
    return {
      filters: {
        branch_id: null,
        customer_id: null,
      },
      branches: [],
      customers: [],
      reportData: [],
      loading: false,
      summaryHeaders: [
        { text: "Customer Name", value: "customer_name" },
        { text: "Total Invoice Amount", value: "total_invoice" },
        { text: "Total Paid Amount", value: "paid_amount" },
        { text: "Total Amount Due", value: "due_amount" },
      ],
    };
  },
  computed: {
    customerSummary() {
      const summary = {};
      this.reportData.forEach((row) => {
        if (!summary[row.customer_name]) {
          summary[row.customer_name] = {
            customer_name: row.customer_name,
            total_invoice: 0,
            paid_amount: 0,
            due_amount: 0,
          };
        }
        summary[row.customer_name].total_invoice += Number(
          row.total_invoice || 0,
        );
        summary[row.customer_name].paid_amount += Number(row.paid_amount || 0);
        summary[row.customer_name].due_amount += Number(row.due_amount || 0);
      });
      return Object.values(summary);
    },
    grandTotals() {
      return this.customerSummary.reduce(
        (totals, row) => {
          totals.total_invoice += row.total_invoice;
          totals.paid_amount += row.paid_amount;
          totals.due_amount += row.due_amount;
          return totals;
        },
        { total_invoice: 0, paid_amount: 0, due_amount: 0 },
      );
    },
  },
  created() {
    this.fetchBranches();
    this.fetchCustomers();
    this.fetchReport();
  },
  methods: {
    async fetchBranches() {
      try {
        const res = await BranchService.getAll();
        this.branches = res.data.data || [];
      } catch {
        this.branches = [];
      }
    },
    async fetchCustomers() {
      try {
        const res = await CustomerService.getAll();
        this.customers = res.data.data || [];
      } catch {
        this.customers = [];
      }
    },
    async fetchReport() {
      this.loading = true;
      try {
        const res = await ReportService.getDueCustomerReport(this.filters);
        this.reportData = res.data.data || [];
      } catch {
        this.reportData = [];
      } finally {
        this.loading = false;
      }
    },
    downloadPDF() {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Customer-wise Due Summary", 14, 15);
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      let y = 22;
      const branch = this.branches.find(
        (b) => String(b.id) === String(this.filters.branch_id),
      );
      const customer = this.customers.find(
        (c) => String(c.id) === String(this.filters.customer_id),
      );
      if (branch) {
        doc.text(`Branch: ${branch.name}`, 14, y);
        y += 7;
      }
      if (customer) {
        doc.text(`Customer: ${customer.company_name}`, 14, y);
        y += 7;
      }
      if (branch || customer) {
        y += 2;
      }
      doc.autoTable({
        head: [this.summaryHeaders.map((h) => h.text)],
        body: this.customerSummary.map((row) => [
          row.customer_name,
          row.total_invoice,
          row.paid_amount,
          row.due_amount,
        ]),
        startY: y,
      });
      // Grand total row
      doc.autoTable({
        head: [[]],
        body: [
          [
            "Grand Total",
            this.grandTotals.total_invoice,
            this.grandTotals.paid_amount,
            this.grandTotals.due_amount,
          ],
        ],
        startY: doc.lastAutoTable.finalY + 2,
        theme: "plain",
      });
      doc.save("customer_due_summary.pdf");
    },
  },
};
</script>
